import { initializeAppsignal, reportErrorToAppsignal } from "./appsignal";
import { logger } from "$lib/utils/logger";

let displayId: string = "";

function initializeErrorReporter(id: string): void {
  displayId = id;
  initializeAppsignal();
}

function reportError(
  message: string,
  error: Error | unknown = new Error(message),
  tags: Record<string, string> = {}
): void {
  logger.error("[ErrorReporter] Reporting error", message, error);
  const errorObj = ensureError(error);
  if (isTransientNetworkError(errorObj)) {
    logger.warn("[ErrorReporter] Skipping transient network error", message);
    return;
  }
  reportErrorToAppsignal(displayId, message, errorObj, tags);
}

function reportComponentError(error: unknown): void {
  logger.error("[ErrorReporter] Reporting component error", error);
  reportError(getComponentName(error), error);
}

function ensureError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (hasErrorShape(error)) {
    const wrapped = new Error(error.message);
    if (typeof error.name === "string") wrapped.name = error.name;
    // Preserve numeric code (e.g. DOMException 22) for the quota check below.
    const { code } = error as { code?: unknown };
    if (typeof code === "number") (wrapped as { code?: number }).code = code;
    return wrapped;
  }
  return new Error(`Unknown Error: ${JSON.stringify(error)}`);
}

// Cross-realm errors (e.g. DOMException from iframe) fail instanceof
function hasErrorShape(
  error: unknown
): error is { message: string; name?: unknown } {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as any).message === "string"
  );
}

// Detect by name/code, not `instanceof QuotaExceededError` — that standalone
// constructor is a modern API absent on WPE (Safari 14).
function isQuotaExceededError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === "QuotaExceededError" || code === 22;
}

function isTransientNetworkError(error: Error): boolean {
  // Quota failures share the Cache.add() message shape but are actionable, so
  // exclude them before the message-prefix rules below.
  if (isQuotaExceededError(error)) return false;
  const msg = error.message ?? "";
  if (error.name === "TypeError" && msg === "Failed to fetch") return true;
  if (msg.startsWith("[FetchTimeout] ")) return true;
  if (error.name === "AbortError") return true;
  if (msg.startsWith("Failed to update a ServiceWorker")) return true;
  if (msg.startsWith("Failed to register a ServiceWorker")) return true;
  if (msg.startsWith("Failed to execute 'add' on 'Cache'")) return true;
  return false;
}

function getComponentName(error: unknown): string {
  return (
    (error as any).component_stack?.find((c: string) =>
      c.includes(".svelte")
    ) ?? "unknown component"
  );
}

export {
  initializeErrorReporter,
  reportError,
  reportComponentError,
  ensureError,
  isQuotaExceededError,
};
